import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useI18n } from '../../i18n/I18nContext';
import api from '../../utils/api';

function AdminCategories() {
  const { user } = useAuth();
  const { t } = useI18n();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', slug: '', icon: '', sort_order: 0, active: true });

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    if (user) loadData();
  }, [user]);

  async function loadData() {
    try {
      const data = await api.get('/admin/categories');
      setCategories(data);
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/admin/categories/${editingId}`, form);
      } else {
        await api.post('/admin/categories', form);
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ name: '', slug: '', icon: '', sort_order: 0, active: true });
      loadData();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  }

  function handleEdit(cat) {
    setForm({
      name: cat.name,
      slug: cat.slug,
      icon: cat.icon || '',
      sort_order: cat.sort_order || 0,
      active: cat.active
    });
    setEditingId(cat.id);
    setShowForm(true);
  }

  async function handleDelete(id) {
    if (!window.confirm(t('admin.deleteCategory'))) return;
    try {
      await api.delete(`/admin/categories/${id}`);
      loadData();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  }

  // Auto-generate slug from name
  function handleNameChange(name) {
    setForm(f => ({
      ...f,
      name,
      slug: editingId ? f.slug : name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    }));
  }

  // Filter categories
  const filteredCategories = categories
    .filter(c => {
      const matchesSearch = !searchTerm ||
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.slug.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' ||
        (filterStatus === 'active' && c.active) ||
        (filterStatus === 'inactive' && !c.active);
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => a.sort_order - b.sort_order);

  if (loading) {
    return <div className="flex justify-center" style={{ padding: '40px 0' }}><div className="spinner"></div></div>;
  }

  return (
    <div>
      {/* Page Header */}
      <div className="flex justify-between items-center mb-4">
        <h1 style={{ fontSize: '20px', fontWeight: '600' }}>{t('admin.categories')}</h1>
        <button onClick={() => { setShowForm(true); setEditingId(null); setForm({ name: '', slug: '', icon: '', sort_order: categories.length, active: true }); }} className="btn btn-primary" style={{ padding: '8px 16px', fontSize: '14px' }}>
          + {t('common.new')}
        </button>
      </div>
        {/* Filters */}
        <div className="card mb-4">
          <div className="flex gap-2 flex-wrap items-end">
            <div style={{ flex: '2', minWidth: '200px' }}>
              <label className="label text-xs">{t('common.search')}</label>
              <input
                type="text"
                className="input"
                placeholder={t('admin.searchByNameSlug')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div style={{ flex: '1', minWidth: '120px' }}>
              <label className="label text-xs">{t('common.status')}</label>
              <select className="input" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="all">{t('common.all')}</option>
                <option value="active">{t('common.active')}</option>
                <option value="inactive">{t('common.inactive')}</option>
              </select>
            </div>
          </div>
          <div className="text-secondary text-xs mt-2">
            {t('common.showing', { count: filteredCategories.length, total: categories.length })} {t('admin.categories').toLowerCase()}
          </div>
        </div>

        {showForm && (
          <div className="card mb-4">
            <h3 style={{ fontSize: '16px', marginBottom: '16px' }}>{editingId ? t('admin.editCategory') : t('admin.newCategory')}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="label">{t('admin.categoryName')} *</label>
                <input type="text" className="input" value={form.name} onChange={(e) => handleNameChange(e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="label">{t('admin.categorySlug')} *</label>
                <input type="text" className="input" value={form.slug} onChange={(e) => setForm(f => ({ ...f, slug: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="label">{t('admin.iconOptional')}</label>
                <input type="text" className="input" value={form.icon} onChange={(e) => setForm(f => ({ ...f, icon: e.target.value }))} placeholder="utensils, car, map, etc." />
              </div>
              <div className="form-group">
                <label className="label">{t('admin.sortOrder')}</label>
                <input type="number" className="input" value={form.sort_order} onChange={(e) => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} />
              </div>
              <div className="form-group">
                <label><input type="checkbox" checked={form.active} onChange={(e) => setForm(f => ({ ...f, active: e.target.checked }))} /> {t('common.active')}</label>
              </div>
              <div className="flex gap-2">
                <button type="submit" className="btn btn-primary">{t('common.save')}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>{t('common.cancel')}</button>
              </div>
            </form>
          </div>
        )}

        {filteredCategories.length === 0 ? (
          <div className="card text-center text-secondary">
            {t('admin.noCategoriesFound')}
          </div>
        ) : (
          filteredCategories.map(c => (
            <div key={c.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div className="flex items-center gap-2">
                  <h3 style={{ fontSize: '14px' }}>{c.name}</h3>
                  {!c.active && <span className="badge" style={{ background: 'var(--error)', color: 'white', fontSize: '10px', padding: '2px 6px' }}>{t('common.inactive')}</span>}
                </div>
                <p className="text-secondary text-xs">{c.slug} • {t('admin.order')}: {c.sort_order}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleEdit(c)} className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: '12px' }}>{t('common.edit')}</button>
                <button onClick={() => handleDelete(c.id)} className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: '12px', color: 'var(--error)' }}>{t('common.delete')}</button>
              </div>
            </div>
          ))
        )}
    </div>
  );
}

export default AdminCategories;
